// The open/close behaviour shared by the top bar's dropdown menus (`AccountMenu.tsx`,
// `DebugMenu.tsx`): closes on an outside click or Escape, and Escape gives focus back to
// the trigger, since it is the keyboard path and focus has to land somewhere real.

import { useEffect, useRef, useState } from 'react';

export function usePopover(): {
  open: boolean;
  setOpen: (open: boolean | ((open: boolean) => boolean)) => void;
  rootRef: React.RefObject<HTMLDivElement>;
  triggerRef: React.RefObject<HTMLButtonElement>;
} {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return { open, setOpen, rootRef, triggerRef };
}
